import React, { useState } from 'react';
import { useLeadExecutions } from '../hooks/useLeadExecutions';
import { updateLeadExecutionStage, updateLeadExecutionSiteExc, updateLeadExecutionProjectValue, disableLeadExecution } from '../services/leadExecutionService';
import { getLeadExecutionColumns } from '../components/leadExecutionColumns';
import { getLeadExecutionFinancialColumns } from '../components/lead_execution_column_2';
import { useAuthStore } from '@/store/authStore';
import { useUsers } from '@/hooks/useUsers';
import { LeadExecutionStats } from '../components/LeadExecutionStats';
import { LeadExecutionFilter } from '../components/LeadExecutionFilter';
import { LeadExecutionTableView } from '../components/LeadExecutionTableView';
import { useNavigate } from 'react-router-dom';
import { ViewLead } from '../../verve-leads/leads/components/ViewLead';
import { EditProjectValueModal } from '../components/EditProjectValueModal';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import toast from 'react-hot-toast';

export const LeadExecutionsScreen = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const leadData = useLeadExecutions(); 
  const { teamMembers: projectManagers } = useUsers({ role: { $in: ['designer', 'senior_designer'] }, limit: 1000 });
  const { teamMembers: designers } = useUsers({ role: { $in: ['designer', 'senior_designer'] }, limit: 1000 });
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [editingExecution, setEditingExecution] = useState(null);
  const [isEditingValue, setIsEditingValue] = useState(false);
  const [leadToDisable, setLeadToDisable] = useState(null);

  const handleStageChange = async (rowId, newStatus) => {
    try {
      await updateLeadExecutionStage(rowId, newStatus);
      leadData.refetch(); 
      toast.success('Stage updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update stage');
    }
  };
  const handleSiteExcChange = async (leadId, newSiteExc) => {
    try {
      await updateLeadExecutionSiteExc(leadId, newSiteExc);
      leadData.refetch();
      toast.success('Site Executive assigned successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to assign Site Executive');
    }
  };

  const handleEditProjectValue = async (id, value) => {
    try {
      setIsEditingValue(true);
      await updateLeadExecutionProjectValue(id, value);
      await leadData.refetch();
      toast.success('Project Value updated successfully');
      setEditingExecution(null);
    } catch (err) {
      toast.error('Failed to update project value');
    } finally {
      setIsEditingValue(false);
    }
  };
  
  const handleDisableLead = (row) => {
    setLeadToDisable({ id: row._id, isDisabled: row.leadId?.isDisabled });
  };

  const confirmDisableLead = async () => {
    if (!leadToDisable) return;
    try {
      await disableLeadExecution(leadToDisable.id);
      leadData.refetch();
      toast.success(leadToDisable.isDisabled ? 'Lead enabled successfully' : 'Lead disabled successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to change lead status');
    } finally {
      setLeadToDisable(null);
    }
  };

  const allColumns1 = getLeadExecutionColumns({ 
    user, 
    projectManagers,
    designers,
    onSiteExcChange: handleSiteExcChange,
    onView: (row) => setSelectedLeadId(row.leadId),
    onEdit: (row) => setEditingExecution(row),
    onDisable: (row) => handleDisableLead(row),
    onAdd: (row, type) => {
      if (type === 'initial_quotation' || type === 'budget') {
        navigate('/budget', { state: { lead: row.leadId } });
      } else if (type === 'order_placing') {
        navigate('/ordering', { state: { lead: row.leadId } });
      }
    }
  });
  const actionsCol = allColumns1.find(c => c.key === 'actions');
  const columns1 = allColumns1.filter(c => c.key !== 'actions');
  const columns2 = getLeadExecutionFinancialColumns({ onStageChange: handleStageChange });
  const columns = [...columns1, ...columns2, actionsCol].filter(Boolean);

  return (
    <div className="flex flex-col gap-4 w-full min-w-0 max-w-full animate-in fade-in duration-700 relative">
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
            Lead Executions
          </h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Manage converted leads and track executions</p>
        </div>
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/1.jpg"
            alt="Lead Executions"
            className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
          />
        </div>
      </div>

      <LeadExecutionStats stats={leadData.stats} />

      <div className="flex flex-col md:flex-row items-center justify-between border-b border-slate-200 px-1 gap-4 mt-2">
        <div className="flex items-center overflow-x-auto no-scrollbar w-full">
          <button
            onClick={() => { leadData.setActiveTab('pending'); leadData.setPage(1); }}
            className={`flex items-center gap-2 px-6 py-3 text-[13px] font-medium transition-all relative whitespace-nowrap ${
              leadData.activeTab === 'pending'
                ? "text-brand-blue border-b-2 border-brand-blue bg-blue-50/30"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => { leadData.setActiveTab('completed'); leadData.setPage(1); }}
            className={`flex items-center gap-2 px-6 py-3 text-[13px] font-medium transition-all relative whitespace-nowrap ${
              leadData.activeTab === 'completed'
                ? "text-brand-blue border-b-2 border-brand-blue bg-blue-50/30"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      <LeadExecutionFilter {...leadData} />

      <LeadExecutionTableView 
        columns={columns}
        executions={leadData.executions}
        isLoading={leadData.isLoading}
        onRowClick={(row) => {
          const status = row.leadId?.leadStatus || row.projectStatus || row.status || 'new';
          const isCompleted = status.toLowerCase() === 'completed';
          const userRole = user?.role?.toLowerCase() || '';
          const isDesigner = ['designer', 'senior_designer'].includes(userRole) || userRole === 'site_executive';
          const isAdminOrOwner = ['admin', 'owner', 'systemadmin'].includes(userRole);

          if (isCompleted && isDesigner && !isAdminOrOwner) {
            toast.error("Designers cannot edit completed executions.");
            return;
          }
          navigate(`/lead-executions/${row._id}`);
        }}
        page={leadData.page}
        setPage={leadData.setPage}
        totalPages={leadData.totalPages}
        totalItems={leadData.totalItems}
      />

      {selectedLeadId && (
        <ViewLead 
          isOpen={!!selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          lead={selectedLeadId}
          user={user}
        />
      )}

      {editingExecution && (
        <EditProjectValueModal
          isOpen={!!editingExecution}
          onClose={() => setEditingExecution(null)}
          execution={editingExecution}
          onSave={handleEditProjectValue}
          isLoading={isEditingValue}
        />
      )}

      <ConfirmationModal
        isOpen={!!leadToDisable}
        onClose={() => setLeadToDisable(null)}
        onConfirm={confirmDisableLead}
        title={leadToDisable?.isDisabled ? "Enable Lead Execution" : "Disable Lead Execution"}
        message={leadToDisable?.isDisabled ? "Are you sure you want to enable this lead?" : "Are you sure you want to disable this lead? It will be grayed out in this list."}
        confirmLabel={leadToDisable?.isDisabled ? "Enable Lead" : "Disable Lead"}
        variant={leadToDisable?.isDisabled ? "success" : "danger"}
      />
    </div>
  );
};
